import type { CRMNoteReference, CRMTaskReference } from "@/types";
import { NoteNotableValues } from "@/types";
import type { MaterialNames } from "@/components/material_symbol_names";
import { getInteractionDisplayName } from "@/components/interactions/_utils";
import { toFormat } from "@/lib/dateHelpers";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { capitalize } from "lodash";
import {
  APPLICATION_TYPE_LABELS,
  STAGE_LABELS,
  type ApplicationType,
  type ApplicationStage
} from "@/pages/people/membership-services/applications/types";
import { getNoteParentLink, formatSourceName } from "@/features/global/components/NotesTable/noteHelpers";

export type Reference = CRMNoteReference | CRMTaskReference;

export const ReferenceTypenames = {
  BackofficePersonProfileDocument: "BackofficePersonProfileDocument",
  CRMEvent: "CRMEvent",
  CRMTaskFacility: "CRMTaskFacility",
  CRMNote: "CRMNote",
  CRMInteraction: "CRMInteraction",
  BackofficeMembershipApplication: "BackofficeMembershipApplication",
  CommerceOrder: "CommerceOrder",
  CommercePayment: "CommercePayment",
  CRMPerson: "CRMPerson",
  BackofficeCareerPlan: "BackofficeCareerPlan"
} as const;

const REFERENCE_TYPE_LABELS: Record<string, string> = {
  [ReferenceTypenames.BackofficePersonProfileDocument]: "Document",
  [ReferenceTypenames.CRMEvent]: "Event",
  [ReferenceTypenames.CRMTaskFacility]: "Facility",
  [ReferenceTypenames.CRMNote]: "Note",
  [ReferenceTypenames.CRMInteraction]: "Interaction",
  [ReferenceTypenames.BackofficeMembershipApplication]: "Application",
  [ReferenceTypenames.CommerceOrder]: "Order",
  [ReferenceTypenames.CommercePayment]: "Payment",
  [ReferenceTypenames.CRMPerson]: "Person",
  [ReferenceTypenames.BackofficeCareerPlan]: "Career Plan"
};

const REFERENCE_TYPE_ICONS: Record<string, MaterialNames> = {
  [ReferenceTypenames.BackofficePersonProfileDocument]: "description",
  [ReferenceTypenames.CRMEvent]: "event",
  [ReferenceTypenames.CRMTaskFacility]: "location_on",
  [ReferenceTypenames.CRMNote]: "sticky_note_2",
  [ReferenceTypenames.CRMInteraction]: "3p",
  [ReferenceTypenames.BackofficeMembershipApplication]: "assignment_ind",
  [ReferenceTypenames.CommerceOrder]: "receipt_long",
  [ReferenceTypenames.CommercePayment]: "attach_money",
  [ReferenceTypenames.CRMPerson]: "person",
  [ReferenceTypenames.BackofficeCareerPlan]: "rubric"
};

const UNTITLED_CAREER_PLAN_TITLE = "Untitled Career Plan";

export function getCareerPlanDisplayName(title?: string | null): string {
  return title?.trim() || UNTITLED_CAREER_PLAN_TITLE;
}

export function getCareerPlanStatusLabel(status?: string | null): string | null {
  if (!status) return null;
  return status
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getReferenceTypeLabel(typename: string, notableType?: string | null): string {
  if (typename === ReferenceTypenames.CRMNote && notableType) {
    const sourceName = formatSourceName(notableType);
    return sourceName ? `${sourceName} Note` : "Note";
  }
  return REFERENCE_TYPE_LABELS[typename] || typename;
}

export function getReferenceIcon(typename: string): MaterialNames {
  return REFERENCE_TYPE_ICONS[typename] || "link";
}

export function getInteractionWithLabel(ref: {
  participants?: { pgaId: string; firstName?: string; lastName?: string }[] | null;
}): string | undefined {
  const participants = ref.participants;
  if (!participants?.length) return undefined;
  if (participants.length === 1) {
    const p = participants[0];
    const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
    return name || undefined;
  }
  return `${participants.length} people`;
}

export function getApplicationDisplayName(applicationType?: string, personName?: string): string {
  const typeLabel = applicationType
    ? (APPLICATION_TYPE_LABELS[applicationType as ApplicationType] ?? applicationType)
    : "Membership";
  return personName ? `${typeLabel} Application – ${personName}` : `${typeLabel} Application`;
}

export function isCRMTaskReference(ref: Reference): ref is CRMTaskReference {
  return (
    ref.__typename === ReferenceTypenames.CRMNote ||
    ref.__typename === ReferenceTypenames.CRMInteraction ||
    ref.__typename === ReferenceTypenames.BackofficeMembershipApplication ||
    ref.__typename === ReferenceTypenames.CommerceOrder ||
    ref.__typename === ReferenceTypenames.CommercePayment ||
    ref.__typename === ReferenceTypenames.CRMPerson ||
    ref.__typename === ReferenceTypenames.CRMEvent ||
    ref.__typename === ReferenceTypenames.CRMTaskFacility ||
    ref.__typename === ReferenceTypenames.BackofficeCareerPlan
  );
}

export function getOrderDisplayName(ref: {
  description?: string;
  person?: { firstName?: string; lastName?: string; displayName?: string; pgaId?: string };
}): string {
  const personName =
    ref.person?.displayName || [ref.person?.firstName, ref.person?.lastName].filter(Boolean).join(" ") || "";
  if (ref.description && personName) return `${ref.description} – ${personName}`;
  return ref.description || personName || "Order";
}

export function getPaymentDisplayName(ref: { payer?: { name: string }; amountCents?: number }): string {
  const amount = ref.amountCents != null ? centsToDollarsFormatted(ref.amountCents) : null;
  const parts = [ref.payer?.name, amount].filter(Boolean);
  return parts.length > 0 ? `Payment – ${parts.join(" · ")}` : "Payment";
}

export function getPersonDisplayName(ref: {
  displayName?: string | null;
  firstName?: string;
  lastName?: string;
  pgaId?: string | null;
}): string {
  const name = ref.displayName || [ref.firstName, ref.lastName].filter(Boolean).join(" ");
  return name || ref.pgaId || "Person";
}

export function getReferenceName(ref: Reference): string {
  if (isCRMTaskReference(ref)) {
    switch (ref.__typename) {
      case ReferenceTypenames.CRMNote:
        return ref.displayTitle || ref.title || "";
      case ReferenceTypenames.CRMInteraction:
        return getInteractionDisplayName(ref, getInteractionWithLabel(ref));
      case ReferenceTypenames.BackofficeMembershipApplication:
        return getApplicationDisplayName(ref.applicationType, ref.person?.displayName);
      case ReferenceTypenames.CommerceOrder:
        return getOrderDisplayName(ref);
      case ReferenceTypenames.CommercePayment:
        return getPaymentDisplayName(ref);
      case ReferenceTypenames.CRMPerson:
        return getPersonDisplayName(ref);
      case ReferenceTypenames.CRMEvent:
        return ref.title || "";
      case ReferenceTypenames.CRMTaskFacility:
        return ref.name || "";
      case ReferenceTypenames.BackofficeCareerPlan:
        return getCareerPlanDisplayName(ref.title);
    }
  }
  if ("filename" in ref && ref.filename) return ref.filename;
  if ("name" in ref && ref.name) return ref.name;
  return "";
}

function getNoteSourceText(ref: { notableType?: string | null; notableName?: string | null }): string | null {
  if (!ref.notableName) return null;
  const label =
    ref.notableType === NoteNotableValues.FACILITY
      ? "Facility"
      : ref.notableType === NoteNotableValues.EVENT
        ? "Event"
        : "Person";
  return `${label}: ${ref.notableName}`;
}

export function getReferenceSubtext(ref: Reference): string {
  if (isCRMTaskReference(ref) && ref.__typename === ReferenceTypenames.CRMNote) {
    return [getNoteSourceText(ref), ref.authorName ? `Author: ${String(ref.authorName)}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  if ("authorName" in ref && ref.authorName) return `Author: ${String(ref.authorName)}`;
  if ("categories" in ref && Array.isArray(ref.categories) && ref.categories.length)
    return `Topics: ${ref.categories.join(", ")}`;
  if (ref.__typename === ReferenceTypenames.BackofficeMembershipApplication && "status" in ref && ref.status) {
    const label = STAGE_LABELS[ref.status as ApplicationStage] ?? ref.status;
    return `Status: ${label}`;
  }
  if (
    (ref.__typename === ReferenceTypenames.CommerceOrder || ref.__typename === ReferenceTypenames.CommercePayment) &&
    "status" in ref &&
    ref.status
  ) {
    return `Status: ${capitalize(ref.status)}`;
  }
  if (ref.__typename === ReferenceTypenames.CRMPerson && "pgaId" in ref && ref.pgaId) {
    return `PGA ID: ${ref.pgaId}`;
  }
  if (ref.__typename === ReferenceTypenames.CRMEvent && "status" in ref && ref.status) {
    return `Status: ${ref.status}`;
  }
  if (isCRMTaskReference(ref) && ref.__typename === ReferenceTypenames.BackofficeCareerPlan) {
    const statusLabel = getCareerPlanStatusLabel(ref.status);
    return statusLabel ? `Status: ${statusLabel}` : "";
  }
  return "";
}

export function getReferenceSupportingText(ref: {
  __typename: string;
  notableType?: string | null;
  notableName?: string | null;
  authorName?: string | null;
  categories?: string[] | null;
  date?: string | null;
  applicationStatus?: string | null;
  orderStatus?: string | null;
  paymentStatus?: string | null;
  personPgaId?: string | null;
  eventStatus?: string | null;
  careerPlanStatus?: string | null;
  facilityCity?: string | null;
  facilityState?: string | null;
}): string {
  const formatted = ref.date ? toFormat(ref.date, "MMM dd, yyyy") : null;
  const firstLine = [getReferenceTypeLabel(ref.__typename, ref.notableType), formatted].filter(Boolean).join(" · ");
  const lines = [firstLine];
  const noteSourceText = ref.__typename === ReferenceTypenames.CRMNote ? getNoteSourceText(ref) : null;
  if (noteSourceText) lines.push(noteSourceText);
  if (ref.authorName) lines.push(`Author: ${ref.authorName}`);
  if (ref.categories?.length) lines.push(`Topics: ${ref.categories.join(", ")}`);
  if (ref.applicationStatus) {
    const statusLabel = STAGE_LABELS[ref.applicationStatus as ApplicationStage] ?? ref.applicationStatus;
    lines.push(`Status: ${statusLabel}`);
  }
  if (ref.orderStatus) lines.push(`Status: ${capitalize(ref.orderStatus)}`);
  if (ref.paymentStatus) lines.push(`Status: ${capitalize(ref.paymentStatus)}`);
  if (ref.__typename === ReferenceTypenames.CRMPerson && ref.personPgaId) lines.push(`PGA ID: ${ref.personPgaId}`);
  if (ref.__typename === ReferenceTypenames.CRMEvent && ref.eventStatus) {
    lines.push(`Status: ${ref.eventStatus}`);
  }
  if (ref.__typename === ReferenceTypenames.CRMTaskFacility) {
    const location = [ref.facilityCity, ref.facilityState].filter(Boolean).join(", ");
    if (location) lines.push(location);
  }
  if (ref.__typename === ReferenceTypenames.BackofficeCareerPlan) {
    const statusLabel = getCareerPlanStatusLabel(ref.careerPlanStatus);
    if (statusLabel) lines.push(`Status: ${statusLabel}`);
  }
  return lines.join("\n");
}

export function getReferenceModifiedDate(ref: Reference): string | null {
  if ("updatedAt" in ref && ref.updatedAt) return ref.updatedAt;
  if ("lastModifiedDate" in ref && ref.lastModifiedDate) return ref.lastModifiedDate;
  if ("paidAt" in ref && ref.paidAt) return ref.paidAt;
  if ("createdAt" in ref) return ref.createdAt;
  return null;
}

export function getReferenceParentLink(ref: Reference): string | null {
  if (!isCRMTaskReference(ref)) return null;

  if (ref.__typename === ReferenceTypenames.CRMNote && ref.notableId) {
    if (ref.notableType === NoteNotableValues.ORDER) {
      return `/billing/orders/${ref.notableId}?highlightNote=${ref.id}`;
    }
    if (ref.notableType === NoteNotableValues.EVENT_REGISTRATION) {
      return getNoteParentLink(
        {
          id: ref.id,
          notableId: ref.notableId,
          notableType: ref.notableType,
          eventSlug: ref.eventSlug
        },
        { pgaId: "", eventSlug: ref.eventSlug ?? undefined }
      );
    }
    if (ref.notableType === NoteNotableValues.PAYMENT) {
      return `/billing/orders/payment/${ref.notableId}?highlightNote=${ref.id}`;
    }
    if (ref.notableType === NoteNotableValues.FACILITY) {
      return `/facilities/${ref.notableId}/details/notes?highlightNote=${ref.id}`;
    }
    if (ref.notableType === NoteNotableValues.EVENT) {
      return getNoteParentLink(
        {
          id: ref.id,
          notableId: ref.notableId,
          notableType: ref.notableType,
          eventSlug: ref.eventSlug
        },
        { pgaId: "" }
      );
    }
    if (ref.notableType === NoteNotableValues.FACILITY_ASSOCIATION) {
      if (!ref.facilityId) return null;
      return `/facilities/${ref.facilityId}/people/governance?expandNotes=${ref.notableId}&highlightNote=${ref.id}`;
    }
    const pgaId = ref.notableType === NoteNotableValues.PERSON ? ref.notableId : ref.personPgaId;
    if (!pgaId) return null;
    return getNoteParentLink(
      { id: ref.id, notableId: ref.notableId, notableType: ref.notableType ?? "" },
      { pgaId, universityLegacyId: ref.universityLegacyId ?? undefined }
    );
  }

  if (ref.__typename === ReferenceTypenames.CRMInteraction && ref.participants?.[0]?.pgaId) {
    return `/people/${ref.participants[0].pgaId}/interactions/interactions/${ref.id}`;
  }

  if (ref.__typename === ReferenceTypenames.BackofficeMembershipApplication) {
    return `/people/membership-services/applications/${ref.id}`;
  }

  if (ref.__typename === ReferenceTypenames.CommerceOrder) {
    return `/billing/orders/${ref.id}`;
  }

  if (ref.__typename === ReferenceTypenames.CommercePayment) {
    return `/billing/orders/payment/${ref.id}`;
  }

  if (ref.__typename === ReferenceTypenames.CRMPerson && ref.pgaId) {
    return `/people/${ref.pgaId}`;
  }

  if (ref.__typename === ReferenceTypenames.CRMEvent && ref.slug) {
    return `/events/${ref.slug}/notes`;
  }

  if (ref.__typename === ReferenceTypenames.CRMTaskFacility) {
    return `/facilities/${ref.id}/details/notes`;
  }

  if (ref.__typename === ReferenceTypenames.BackofficeCareerPlan && ref.pgaId) {
    return `/people/${ref.pgaId}/career/career-profile/plans/${ref.id}`;
  }

  return null;
}

export type TaskReferenceParentKind = "person" | "facility" | "event";

export type TaskReferenceParent = {
  kind: TaskReferenceParentKind;
  key: string;
  name: string;
  pgaId?: string | null;
  facilityId?: string | null;
  eventSlug?: string | null;
};

function parentPersonName(
  person?: {
    displayName?: string | null;
    firstName?: string;
    lastName?: string;
  } | null
): string {
  if (!person) return "";
  return (person.displayName || [person.firstName, person.lastName].filter(Boolean).join(" ")).trim();
}

function parentsAreSameIdentity(existing: TaskReferenceParent, incoming: TaskReferenceParent): boolean {
  if (existing.key === incoming.key) return true;
  if (existing.kind !== incoming.kind) return false;
  if (incoming.pgaId && existing.pgaId === incoming.pgaId) return true;
  if (incoming.facilityId && existing.facilityId === incoming.facilityId) return true;
  if (incoming.eventSlug && existing.eventSlug === incoming.eventSlug) return true;
  const existingHasId = Boolean(existing.pgaId || existing.facilityId || existing.eventSlug);
  const incomingHasId = Boolean(incoming.pgaId || incoming.facilityId || incoming.eventSlug);
  if (existingHasId && incomingHasId) return false;
  return existing.name === incoming.name;
}

function addTaskReferenceParent(parents: TaskReferenceParent[], parent: TaskReferenceParent): void {
  if (!parent.name) return;

  const existing = parents.find((candidate) => parentsAreSameIdentity(candidate, parent));
  if (existing) {
    if (parent.pgaId && !existing.pgaId) existing.pgaId = parent.pgaId;
    if (parent.facilityId && !existing.facilityId) existing.facilityId = parent.facilityId;
    if (parent.eventSlug && !existing.eventSlug) existing.eventSlug = parent.eventSlug;
    if (parent.pgaId) existing.key = `person:${parent.pgaId}`;
    else if (parent.facilityId) existing.key = `facility:${parent.facilityId}`;
    else if (parent.eventSlug) existing.key = `event:${parent.eventSlug}`;
    return;
  }

  parents.push(parent);
}

export function getTaskReferenceParents(references: Reference[] | null | undefined): TaskReferenceParent[] {
  const parents: TaskReferenceParent[] = [];
  if (!references?.length) return parents;

  for (const ref of references) {
    if (!isCRMTaskReference(ref)) continue;

    switch (ref.__typename) {
      case ReferenceTypenames.CRMPerson: {
        addTaskReferenceParent(parents, {
          kind: "person",
          key: `person:${ref.pgaId || ref.id}`,
          name: parentPersonName(ref),
          pgaId: ref.pgaId ?? null
        });
        break;
      }
      case ReferenceTypenames.CRMTaskFacility: {
        addTaskReferenceParent(parents, {
          kind: "facility",
          key: `facility:${ref.id}`,
          name: ref.name?.trim() ?? "",
          facilityId: ref.id
        });
        break;
      }
      case ReferenceTypenames.CRMEvent: {
        addTaskReferenceParent(parents, {
          kind: "event",
          key: `event:${ref.slug || ref.id}`,
          name: ref.title?.trim() ?? "",
          eventSlug: ref.slug || null
        });
        break;
      }
      case ReferenceTypenames.CRMNote: {
        const notableName = ref.notableName?.trim() ?? "";
        if (!notableName) break;

        if (ref.notableType === NoteNotableValues.FACILITY) {
          addTaskReferenceParent(parents, {
            kind: "facility",
            key: `facility:${ref.notableId || notableName}`,
            name: notableName,
            facilityId: ref.notableId ?? null
          });
          break;
        }

        if (ref.notableType === NoteNotableValues.EVENT) {
          addTaskReferenceParent(parents, {
            kind: "event",
            key: `event:${ref.eventSlug || ref.notableId || notableName}`,
            name: notableName,
            eventSlug: ref.eventSlug ?? null
          });
          break;
        }

        const pgaId =
          ref.notableType === NoteNotableValues.PERSON ? (ref.notableId ?? ref.personPgaId) : ref.personPgaId;
        addTaskReferenceParent(parents, {
          kind: "person",
          key: `person:${pgaId || notableName}`,
          name: notableName,
          pgaId: pgaId ?? null
        });
        break;
      }
      case ReferenceTypenames.CRMInteraction: {
        for (const participant of ref.participants ?? []) {
          const name = [participant.firstName, participant.lastName].filter(Boolean).join(" ").trim();
          addTaskReferenceParent(parents, {
            kind: "person",
            key: `person:${participant.pgaId || name}`,
            name,
            pgaId: participant.pgaId ?? null
          });
        }
        break;
      }
      case ReferenceTypenames.BackofficeMembershipApplication: {
        const name = ref.person?.displayName?.trim() ?? "";
        addTaskReferenceParent(parents, {
          kind: "person",
          key: `person:${ref.person?.pgaId || name}`,
          name,
          pgaId: ref.person?.pgaId ?? null
        });
        break;
      }
      case ReferenceTypenames.CommerceOrder: {
        const name = parentPersonName(ref.person);
        addTaskReferenceParent(parents, {
          kind: "person",
          key: `person:${ref.person?.pgaId || name}`,
          name,
          pgaId: ref.person?.pgaId ?? null
        });
        break;
      }
      case ReferenceTypenames.CommercePayment: {
        const name = ref.payer?.name?.trim() ?? "";
        addTaskReferenceParent(parents, {
          kind: "person",
          key: `payer:${ref.payer?.id || name}`,
          name,
          pgaId: null
        });
        break;
      }
      case ReferenceTypenames.BackofficeCareerPlan:
        break;
    }
  }

  return parents;
}
